////////////////////////////////////////////
// LAUNCH READS — LISTS, DETAIL, POSITION, SCORE (TANSTACK QUERY HOOKS)
////////////////////////////////////////////
//
// POLLING INTERVALS MIRROR THE VANILLA APP: LISTS 20s, DETAIL 5s, POSITION
// 15s; SCORE/AUDIT/CREATOR ARE ONE-SHOT (SERVER CACHES 30-45s). ALL READS
// ARE NET-SCOPED — QUERY KEYS ALWAYS INCLUDE THE NET STRING SO A NET
// SWITCH NEVER SHOWS STALE DATA.
import { useQuery } from "@tanstack/react-query";
import { getJson } from "@/lib/api/client";
import {
  rehydrateLaunch, rehydrateRhLaunch, rehydrateAuctionV3, rehydrateInstantV3,
  type Audit, type AuctionV3, type AuctionV3Wire, type CreatorPanelWire, type InstantV3, type InstantV3Wire, type ParticipationWire, type Position, type RhLaunchWire, type Score, type SolLaunchWire,
} from "@/lib/api/types";
import { RH_LAUNCH_KEY_RE, RH_V3_KEY_RE } from "@/config/network";

// POLL INTERVALS (MS) — ONE PLACE, SAME NUMBERS AS web/app
export const POLL = {
  LIST: 20_000,
  DETAIL: 5_000,
  POSITION: 15_000,
  MARKET: 20_000,
} as const;

// WIRE LIST — EITHER CHAIN'S LAUNCHES, DISCRIMINATED BY KEY SHAPE DOWNSTREAM.
// U-I6: widened to include the two V3 curve shapes (auction-v3/instant-v3) —
// handleLaunchList already folds both into this same array, tagged with
// `kind`, alongside the fair/instant rows (web/server/routes/launch.routes.mjs).
type LaunchListWire = { launches: (SolLaunchWire | RhLaunchWire | AuctionV3Wire | InstantV3Wire)[] };

// LIST — ALL LAUNCHES ON A NET (RAW WIRE SHAPE; PAGES REHYDRATE AS NEEDED)
export function useLaunches(net: string, opts: { owner?: string | null; enabled?: boolean } = {}) {

  const ownerQ = opts.owner ? "&owner=" + encodeURIComponent(opts.owner) : "";
  return useQuery({
    queryKey: ["launches", net, opts.owner ?? null],
    queryFn: () => getJson<LaunchListWire>("/api/launches?net=" + net + ownerQ),
    refetchInterval: POLL.LIST,
    enabled: opts.enabled !== false,
  });
}

// WALLET COMMITS — ONE WALLET'S FAIR-LAUNCH COMMIT HISTORY, LAUNCH HYDRATED
// (MY LAUNCHES' BOUGHT VIEW). BOTH CHAINS (SEE ParticipationWire); SAME
// 20s POLL AS THE LIST, DISABLED UNTIL A WALLET IS ACTUALLY CONNECTED — THE
// ENDPOINT 400s WITHOUT AN owner, SO THIS NEVER FIRES ON A BARE MOUNT.
export function useWalletCommits(net: string, opts: { owner?: string | null; enabled?: boolean } = {}) {

  return useQuery({
    queryKey: ["wallet-commits", net, opts.owner ?? null],
    queryFn: () => getJson<{ participations: ParticipationWire[] }>(
      "/api/wallet/" + encodeURIComponent(opts.owner!) + "/commits?net=" + net),
    refetchInterval: POLL.LIST,
    enabled: !!opts.owner && opts.enabled !== false,
  });
}

// DETAIL — ONE LAUNCH, KEY SHAPE PICKS THE REHYDRATOR
export function useLaunch(key: string | null, net: string) {

  return useQuery({
    queryKey: ["launch", net, key],
    queryFn: async () => {

      const j = await getJson<SolLaunchWire | RhLaunchWire>(
        "/api/launch/" + encodeURIComponent(key!) + "?net=" + net);
      return RH_LAUNCH_KEY_RE.test(key!)
        ? { chain: "rh" as const, launch: rehydrateRhLaunch(j as RhLaunchWire) }
        : { chain: "solana" as const, launch: rehydrateLaunch(j as SolLaunchWire) };
    },
    refetchInterval: POLL.DETAIL,
    enabled: !!key,
  });
}

// V3 CURVE DETAIL — DEDICATED HOOK, NOT A useLaunch BRANCH (U-F1): LaunchPage
// dispatches to this directly on launchVenue(key) === "rh-v3" rather than
// routing a V3 key through useLaunch's RH_LAUNCH_KEY_RE discriminator (which
// would miss a bare V3 key entirely — see U-F0's normalize.ts fix for the
// same shape mismatch on the card-grid side). enabled REQUIRES the V3 shape
// specifically (not just !!key) since this hook has no internal chain
// dispatch of its own to fall back on.
//
// U-I6 (docs/instant-curve-build-plan.md): a bare-0x V3 key is now
// STRUCTURALLY AMBIGUOUS between an auction-v3 launch and an instant-v3 one
// (curve/token addresses have the identical shape — see config/network.ts's
// launchVenue, which is deliberately shape-only and does NOT resolve this).
// The server already resolves the ambiguity for us: handleLaunchState tries
// auction_v3_launches first, falls back to instant_v3_launches on a miss,
// and tags the response `kind` accordingly (web/server/routes/launch.routes.mjs).
// So this hook makes ONE fetch (never two — the whole point of the
// server-side fallback is to avoid the frontend having to guess/double-fetch)
// and picks the rehydrator off the response's own `kind` field, returning a
// discriminated union LaunchPage can branch on directly. Renamed from
// useAuctionV3 (its one call site, LaunchPage.tsx, is updated alongside this)
// since it no longer exclusively serves the auction lane.
export type V3LaunchResult =
  | { kind: "auction-v3"; data: AuctionV3 }
  | { kind: "instant-v3"; data: InstantV3 };

export function useV3Launch(key: string | null, net: string) {

  return useQuery({
    queryKey: ["v3-launch", net, key],
    queryFn: async (): Promise<V3LaunchResult> => {

      const j = await getJson<(AuctionV3Wire | InstantV3Wire) & { kind?: "auction-v3" | "instant-v3" }>(
        "/api/launch/" + encodeURIComponent(key!) + "?net=" + net);
      // Default to auction-v3 if `kind` is ever somehow absent — matches
      // this hook's own pre-U-I6 behavior, so a server response predating
      // this change (or any edge case) degrades to the exact prior shape.
      if (j.kind === "instant-v3") return { kind: "instant-v3", data: rehydrateInstantV3(j as InstantV3Wire) };
      return { kind: "auction-v3", data: rehydrateAuctionV3(j as AuctionV3Wire) };
    },
    refetchInterval: POLL.DETAIL,
    enabled: !!key && RH_V3_KEY_RE.test(key),
  });
}

// POSITION — THE CONNECTED WALLET'S COMMITMENT IN A LAUNCH (BOTH CHAINS;
// ENRICHED LEDGER FIELDS ARE OPTIONAL — SEE Position)
export function usePosition(key: string | null, owner: string | null, net: string) {

  return useQuery({
    queryKey: ["position", net, key, owner],
    queryFn: () => getJson<Position>(
      "/api/launch/" + encodeURIComponent(key!) + "/position?owner=" + encodeURIComponent(owner!) + "&net=" + net),
    refetchInterval: POLL.POSITION,
    enabled: !!key && !!owner,
  });
}

// AUDIT — TRUST-GRID AGGREGATES. ONE-SHOT (staleTime, NO INTERVAL): THE
// NUMBERS DON'T NEED TO BE LIVE, AND KEEPING IT OFF THE POLL GRID KEEPS
// THE FROZEN-CLOCK VISUAL TESTS' SETTLE INSTANT QUIET. 404/ERROR DEGRADES
// TO undefined — THE GRID FALLS BACK TO MECHANISM TICKS.
export function useAudit(key: string | null, net: string) {

  return useQuery({
    queryKey: ["audit", net, key],
    queryFn: () => getJson<Audit>("/api/launch/" + encodeURIComponent(key!) + "/audit?net=" + net),
    staleTime: 45_000,
    retry: false,
    enabled: !!key,
  });
}

// CREATOR PANEL — FETCHED ONLY WHEN THE CONNECTED WALLET IS THE AUTHORITY
// (CALLERS GATE VIA enabled); SAME ONE-SHOT/DEGRADE SHAPE AS useAudit.
export function useCreator(key: string | null, net: string, enabled: boolean) {

  return useQuery({
    queryKey: ["creator", net, key],
    queryFn: () => getJson<CreatorPanelWire>("/api/launch/" + encodeURIComponent(key!) + "/creator?net=" + net),
    staleTime: 45_000,
    retry: false,
    enabled: !!key && enabled,
  });
}

// SCORE — REPUTATION BADGES (SERVER CACHES 45s; NO CLIENT POLL)
export function useScore(key: string | null, net: string) {

  return useQuery({
    queryKey: ["score", net, key],
    queryFn: () => getJson<Score>("/api/score/" + encodeURIComponent(key!) + "?net=" + net),
    staleTime: 45_000,
    enabled: !!key,
  });
}
